import type {Lambda} from "mobx";
import type {FunctionComponent, SVGProps} from "react";
import * as React from "react";
import {observer} from "mobx-react";
import {observable, makeObservable} from "mobx";
import styled from "styled-components";
import {withAppStores} from "../../../AppStores";
import type {AppState} from "../../../data/state/AppState";
import type {IModel} from "../../../data/models/Model";
import type {TransportLayer} from "../../../data/TransportLayer";
import type {XyiconDto} from "../../../generated/api/base";
import type {App} from "../../../App";
import type {PermissionSet} from "../../../data/models/permission/PermissionSet";
import type {Catalog} from "../../../data/models/Catalog";
import type {IIconConfig} from "../../modules/catalog/create/CatalogTypes";
import type {Boundary} from "../../../data/models/Boundary";
import type {BoundarySpaceMap} from "../../../data/models/BoundarySpaceMap";
import type {Xyicon} from "../../../data/models/Xyicon";
import {CatalogIconType, Permission, XyiconFeature} from "../../../generated/api/base";
import {ScreenType} from "../../modules/abstract/ScreenType";
import {Debouncer} from "../../../utils/function/Debouncer";
import {ObjectUtils} from "../../../utils/data/ObjectUtils";
import {XHRLoader} from "../../../utils/loader/XHRLoader";
import {notify} from "../../../utils/Notify";
import {NotificationType} from "../../notification/Notification";
import {filterModels} from "../../../data/models/filter/Filter";
import {SpotlightRuleEvaluator} from "../spotlight/SpotlightRuleEvaluator";
import {KeyboardListener} from "../../../utils/interaction/key/KeyboardListener";
import {DockableV5} from "../modules/spaceeditor/dockable/DockableV5";
import {Functions} from "../../../utils/function/Functions";
import {ReactUtils} from "../../utils/ReactUtils";
import {ActionBarV5} from "../actionbar/ActionBarV5";
import {featureTitlePlurals} from "../../../data/state/AppStateConstants";
import {WarningWindowV5} from "../popup/WarningWindowV5";
import {PopupUtilsV5} from "../popup/PopupUtilsV5";
import {ViewSharingPopup} from "../sharing/ViewSharingPopup";
import {VerticalFlexStyle} from "../styles/styles";
import {DetailsContentV5} from "../details/DetailsContentV5";
import {CardViewV5} from "../modules/CardViewV5";
import {colorPalette} from "../styles/colorPalette";
import {ConfirmWindowV5} from "../popup/ConfirmWindowV5";
import {extendActionBarButtonsWithDetailsButton, openWorkspaceFromPortfolioModule} from "../modules/ActionBarUtils";
import {SplitterV5} from "../layout/SplitterV5";
import {
	SPLITTER_V5_CATALOG_MAIN_SECOND_PANE_MIN_WIDTH_PX,
	SPLITTER_V5_LOCAL_STORAGE_KEY_SPACE_EDITOR_DOCK_MAIN,
	ensureSpaceEditorDockMainSeededFromLegacyCatalogKey,
} from "../layout/splitterLayoutMath";
import {CreateSpaceUtils} from "../modules/space/CreateSpaceUtils";
import {LoaderV5} from "../loader/LoaderV5";
import {CatalogCreateV5} from "../modules/catalog/CatalogCreateV5";
import {PopupWindowV5} from "../popup/PopupWindowV5";
import {StringUtils} from "../../../utils/data/string/StringUtils";
import {ReportScope} from "../../../generated/api/reports";
import {GlobalSharingPopup} from "../sharing/GlobalSharingPopup";
import {extendLocalOrgSearchModel} from "../topbar/OrganizationWideSearchUtils";
import type {Report} from "../../../data/models/Report";
import {onWorkspaceViewClick} from "../topbar/ViewTabsCommon";
import {GridViewV5} from "./GridViewV5";
import type {TableV5} from "./table/TableV5";
import {ManageColumnsPanelV5} from "./table/ManageColumnsPanelV5";
import {CatalogItemPanelV5} from "./view/catalog/CatalogItemPanelV5";
import {Panel} from "./Panel";

interface ICreateUnplottedXyiconParam {
	catalogId: string;
	parentXyiconId: string | null;
}

export interface IModulePanelProps {
	appState?: AppState;
	transport?: TransportLayer;
	onClose: (createdId?: string) => void;
}

export interface IActionBarItem<T extends IModel> {
	id:
		| "add"
		| "delete"
		| "duplicate"
		| "open"
		| "play"
		| "edit"
		| "import"
		| "manageVersionSets"
		| "details"
		| "id"
		| "unplot"
		| "locate"
		| "fieldUpdate"
		| "favorite"
		| "share";
	title: string;
	label?: string;
	className?: string;
	IconComponent: FunctionComponent<SVGProps<SVGSVGElement> & {title?: string}>;
	isActive?: boolean;
	isVisible?: (selectedItems: T[]) => boolean;
	componentFactory?: (selectedItems: T[]) => React.JSX.Element;
	isEnabled?: (selectedItems: T[]) => boolean;
	onClick?: (selectedItems: T[]) => void;
}

interface IModuleViewProps<T extends IModel> {
	readonly feature: XyiconFeature;
	// You either handle this state outside of the ModuleView, and then you should provide this,
	// along with "setFocusedItems", or you omit it, and then it will be handled in internal state
	readonly focusedItems?: T[];
	// You either handle this state outside of the ModuleView, and then you should provide this,
	// along with "setSelectedItems", or you omit it, and then it will be handled in internal state
	readonly selectedItems?: T[];
	readonly className?: string;
	readonly actionBar?: IActionBarItem<T>[];
	readonly createButtonRef?: React.RefObject<HTMLDivElement>;
	readonly emptyListText?: React.ReactNode;
	readonly isCatalogPanelDocked?: boolean;
	readonly isCatalogPanelOpen?: boolean;
	readonly manageColumns?: boolean;
	readonly manageDetailsPanel?: boolean;
	readonly isSideGrid?: boolean; // next to the spaceeditor
	readonly transport?: TransportLayer;
	readonly app?: App;
	readonly appState?: AppState;
	readonly registerClosePopupsHandler?: (closeHandler: () => void) => void;

	readonly setFocusedItems?: (items: T[]) => void;
	readonly setSelectedItems?: (items: T[]) => void;
	readonly create?: (onClose: () => void) => React.ReactElement;
	readonly onAdd?: () => void;
	readonly onEdit?: (model: T) => void;
	readonly filterPermissions?: (model: T) => boolean;
	readonly setCatalogPanelDocked?: (isDocked: boolean) => void;
	readonly setCatalogPanelOpen?: (isOpen: boolean) => void;
	readonly canDeleteReports?: (selectedItems: T[]) => boolean;
	readonly runReport?: (reports: Report[]) => Promise<void>;
	readonly detailsHeaderControls?: (reports: IModel[]) => React.ReactNode;
}

interface IModuleViewState<T extends IModel> {
	focusedItems: T[];
	selectedItems: T[];
	creating: boolean;
	managingColumns: boolean;
	searchString: string;
	isSearching: boolean;
	isCellContentsWrappingOn: boolean;
	initialLoading: boolean;
	isLoadingDependencies: boolean;
	sharingViewId: string;
	iconConfig: IIconConfig;
	editedCatalog: Catalog; // the icon of this catalog is being edited
	catalogMode: "create" | "edit" | null;
	isFeatureImportPanelOpen: boolean;
	loadingViewId: string;
	isPortTemplateEditorOpen: boolean;
	isGlobalActionPanelOpen: boolean;
}

@withAppStores("navigation", "transport", "appState", "app")
@observer
export class ModuleViewV5<T extends IModel = IModel> extends React.Component<IModuleViewProps<T>, IModuleViewState<T>> {
	public static readonly defaultProps: Partial<IModuleViewProps<IModel>> = {
		manageDetailsPanel: true,
		isSideGrid: false,
	};

	private _pendingTableFocus: {item: T; feature: XyiconFeature} | null = null;

	private _isLoadingCatalogEditor: boolean = false;

	private _appState: AppState;

	private _isDeletePopupWindowOpen: boolean = false;
	private readonly _gridView = React.createRef<GridViewV5<T>>();
	private readonly _detailsContent = React.createRef<DetailsContentV5>();
	private readonly _manageColumnsButtonRef = React.createRef<HTMLDivElement>();
	private _disposer: Lambda;
	private _portfolioId: string;

	private _creationCount = 0;
	private _isMounted: boolean = false;

	private readonly _spotlightEvaluator = new SpotlightRuleEvaluator<T>();

	private get selectedScreenType(): ScreenType {
		return this.props.feature === XyiconFeature.Space ? ScreenType.CARD : ScreenType.GRID;
	}

	private readonly _actionBar: {
		handlers: {
			[action: string]: () => void;
		};
		enabled?: {
			[action: string]: (items: T[]) => boolean;
		};
	};

	private readonly _listDebouncer = new Debouncer(1000);
	private readonly _typingDebouncer = new Debouncer(1000);

	// We assume ModuleView constructor runs every time a module is opened (is the case currently)
	private _lastViewId: string = "";

	constructor(props: IModuleViewProps<T>) {
		super(props);
		makeObservable<this, "_isLoadingCatalogEditor">(this, {
			_isLoadingCatalogEditor: observable,
		});
		this._appState = props.appState;
		this._portfolioId = this._appState.activePortfolioId;
		const list = this._appState.lists[props.feature];
		this.state = {
			focusedItems: [],
			selectedItems: [],
			creating: false,
			managingColumns: false,
			searchString: "",
			isSearching: false,
			// It is always true for now, maybe this will be changed in the future
			isCellContentsWrappingOn: true,
			initialLoading: list?.array.length > 100,
			isLoadingDependencies: !list.loaded, // we set it to false when all the dependencies are loaded 100%
			sharingViewId: null,
			editedCatalog: null,
			iconConfig: null,
			catalogMode: null,
			isFeatureImportPanelOpen: false,
			loadingViewId: "",
			isPortTemplateEditorOpen: false,
			isGlobalActionPanelOpen: false,
		};

		props.app.moduleViews[props.feature] = this;

		this._actionBar = {
			handlers: {
				add: this.onAddClick,
				import: this.onImportClick,
				duplicate: this.onActionBarDuplicateClick,
				edit: this.onActionBarEditClick,
				delete: this.onDeleteClick,
				locate: this.locateItemInSpaceEditor,
			},
			enabled: {
				add: Functions.returnTrue,
				import: this.canImport,
				duplicate: (items) => items.length === 1,
				edit: (items) => items.length === 1,
				delete: (items) => items.length > 0,
				manageVersionSets: Functions.returnTrue,
			},
		};
	}

	private get selectedItems() {
		return this.props.selectedItems ?? this.state.selectedItems;
	}

	private get focusedItems() {
		return this.props.focusedItems ?? this.state.focusedItems;
	}

	private readonly setSelectedItems = (items: T[]) => {
		return this.props.setSelectedItems ? this.props.setSelectedItems(items) : this.setState({selectedItems: items});
	};

	private readonly setFocusedItems = (items: T[]) => {
		return this.props.setFocusedItems ? this.props.setFocusedItems(items) : this.setState({focusedItems: items});
	};

	private readonly locateItemInSpaceEditor = () => {
		this._appState.actions.navigateToSpaceItem(this.selectedItems[0] as IModel as Boundary | BoundarySpaceMap | Xyicon, true);
	};

	private readonly onCloseGridActionBar = () => {
		this.setSelectedItems([]);
		this.setFocusedItems([]);
	};

	private readonly onCloseCardActionBar = () => {
		this.setSelectedItems([]);
		this.setFocusedItems([]);
	};

	private readonly getExistingItemInListMaybe = (id: string): T | null => {
		const unfilteredItems: T[] = this._appState.actions.getList(this.props.feature);
		// `item` might be coming from "Organization Wide Search - #10779", from a different portfolio,
		// so it might be a different object with same (or similar) data
		// So we need to find it in the recently refreshed list, otherwise it won't find it in the table
		return unfilteredItems.find((obj) => obj.id === id);
	};

	private readonly waitForListToLoad = () => {
		if (!this.isLoadingList) {
			return Promise.resolve();
		}

		return new Promise<void>((resolve) => {
			const waitForLoad = () => {
				if (!this._isMounted || !this.isLoadingList) {
					resolve();
					return;
				}

				window.setTimeout(waitForLoad, 20);
			};

			waitForLoad();
		});
	};

	private readonly getVisibleItemInCurrentViewMaybe = (item: T): T | null => {
		const unfilteredItems: T[] = this._appState.actions.getList(this.props.feature);
		const items = this.getFilteredItems(unfilteredItems);
		let visibleItems = items;

		if (this.state.searchString) {
			visibleItems = this._appState.actions.searchModelsCached(items, this.state.searchString, this.props.feature);
		}

		return visibleItems.find((visibleItem) => visibleItem.id === item.id) ?? null;
	};

	private readonly getVisibleItemsInCurrentView = (items: T[]): T[] => {
		return items
			.map((item) => {
				const existingItemInListMaybe = this.getExistingItemInListMaybe(item.id);
				return this.getVisibleItemInCurrentViewMaybe(existingItemInListMaybe ?? item);
			})
			.filter((item): item is T => !!item);
	};

	private readonly syncSelectionWithCurrentView = () => {
		const visibleSelectedItems = this.getVisibleItemsInCurrentView(this.selectedItems);
		const visibleFocusedItems = this.getVisibleItemsInCurrentView(this.focusedItems);
		const currentSelectedIds = this.selectedItems.map((item) => item.id).sort();
		const nextSelectedIds = visibleSelectedItems.map((item) => item.id).sort();
		const currentFocusedIds = this.focusedItems.map((item) => item.id).sort();
		const nextFocusedIds = visibleFocusedItems.map((item) => item.id).sort();

		if (!ObjectUtils.compare(currentSelectedIds, nextSelectedIds)) {
			this.setSelectedItems(visibleSelectedItems);
		}

		if (!ObjectUtils.compare(currentFocusedIds, nextFocusedIds)) {
			this.setFocusedItems(visibleFocusedItems);
		}
	};

	private readonly notifyItemHiddenByCurrentView = () => {
		notify(this.props.app.notificationContainer, {
			type: NotificationType.Warning,
			title: "Item Hidden by Current View",
			description: "The details panel is open, but this item is hidden by the current view filters or search.",
			lifeTime: 10000,
		});
	};

	/** Navigate the current table without changing selection or focus. */
	public readonly scrollToItem = (item: T) => {
		this._pendingTableFocus = null;
		if (this.table) {
			this.table.goToItem(item);
			return;
		}

		this._pendingTableFocus = {item, feature: this.props.feature};
	};

	private readonly onTableReady = (table: TableV5<T>): void => {
		const pending = this._pendingTableFocus;
		this._pendingTableFocus = null;
		if (this._isMounted && pending?.feature === this.props.feature) {
			table.goToItem(pending.item);
		}
	};

	public async focusItem(item: T) {
		await this.waitForListToLoad();

		if (!this._isMounted) {
			return;
		}
		this._pendingTableFocus = null;

		const existingItemInListMaybe = this.getExistingItemInListMaybe(item.id);
		const itemToFocus = existingItemInListMaybe ?? item;
		const visibleItemMaybe = this.getVisibleItemInCurrentViewMaybe(itemToFocus);

		this.setFocusedItems([itemToFocus]);

		if (this.selectedScreenType === ScreenType.CARD) {
			this.setSelectedItems(visibleItemMaybe ? [visibleItemMaybe] : []);
		}

		if (visibleItemMaybe && this.selectedScreenType !== ScreenType.CARD) {
			this.scrollToItem(visibleItemMaybe);
		} else if (!visibleItemMaybe) {
			this.notifyItemHiddenByCurrentView();
		}
	}

	private readonly toggleSharingPanel = (viewId?: string) => {
		this.setState({sharingViewId: viewId});
	};

	private readonly onManageColumns = () => {
		this.setState({managingColumns: true});
	};

	private readonly onCloseManageColumns = () => {
		this.setState({managingColumns: false});
	};

	private readonly onCloseCreate = (createdId?: string) => {
		if (createdId) {
			const {feature} = this.props;

			this.onFocus(
				this._appState.actions
					.getList(feature)
					.filter((item) => item.id === createdId) // TODO optimize
					.filter((item) => Boolean(item)) as T[],
			);

			this.table?.scrollToItemId(createdId);
		}
	};

	private readonly refreshList = async () => {
		const {feature, transport} = this.props;

		this.setSelectedItems([]);
		this.setFocusedItems([]);

		this.setState({
			isLoadingDependencies: true,
		});

		await transport.services.feature.refreshList(feature);

		this.setState({
			isLoadingDependencies: false,
		});
	};

	private readonly onSelect = (items: T[]) => {
		this.setSelectedItems(items);
	};

	private readonly onFocus = async (items: T[], isGoToItemNeeded: boolean = true) => {
		this.setFocusedItems(items);

		if (items.length) {
			// is there anything that need the items[0] if items.length > 1?
			if (isGoToItemNeeded) {
				this.table?.goToItem(items[items.length - 1]);
			}
		}

		if (items.length === 1) {
			if (items[0].ownFeature === XyiconFeature.Portfolio) {
				if (items[0].id !== this._appState.activePortfolioId) {
					this._detailsContent.current?.setState({isSpaceListLoading: true});
					await extendLocalOrgSearchModel(this._appState, items[0]);
					this._detailsContent.current?.setState({isSpaceListLoading: false});
				}
			}
		}
	};

	private readonly onCreateUnplottedXyicons = async (params: ICreateUnplottedXyiconParam[]) => {
		const {result, error} = await this.props.transport.requestForOrganization<XyiconDto>({
			url: "xyicons/createunplotted",
			method: XHRLoader.METHOD_PUT,
			params: {
				portfolioID: this._appState.activePortfolioId,
				xyiconCreateDetail: params.map((p) => ({xyiconCatalogID: p.catalogId, parentXyiconID: p.parentXyiconId, fieldValues: {}})),
			},
		});

		if (Array.isArray(result) && result.length > 0) {
			const newXyicons: T[] = [];

			for (const element of result) {
				newXyicons.push(this._appState.actions.addToList(element, XyiconFeature.Xyicon) as T);
			}
			const filteredItems = this.getFilteredItems(newXyicons);

			if (filteredItems.length < result.length) {
				notify(this.props.app.notificationContainer, {
					lifeTime: Infinity,
					type: NotificationType.Warning,
					title: "Current view will hide your new xyicon!",
					description:
						"The current view prevents your new xyicon from displaying on the space. Click Edit Details and change the fields to make your new xyicon visible.",
					buttonLabel: "Edit Details",
					onActionButtonClick: () => this.onFocus(newXyicons),
				});
			} else {
				this.onFocus(newXyicons);
			}
		} else {
			notify(this.props.app.notificationContainer, {
				type: NotificationType.Error,
				title: "Error",
				description: "Sorry, something went wrong",
				lifeTime: Infinity,
			});
		}
	};

	private readonly onDoubleClick = (item: T) => {
		if (item.ownFeature === XyiconFeature.Space) {
			const selectedView = this._appState.actions.getSelectedView(XyiconFeature.SpaceEditor);

			onWorkspaceViewClick(selectedView, true, undefined, {spaceRefId: item.refId});
		} else if (item.ownFeature === XyiconFeature.Portfolio) {
			openWorkspaceFromPortfolioModule(this._appState, item.id);
		} else if (item.ownFeature === XyiconFeature.Boundary || item.ownFeature === XyiconFeature.Xyicon) {
			this._appState.actions.navigateToSpaceItemById(item.id, item.ownFeature, true);
		}
	};

	private getFilteredItems(unfilteredItems: T[]): T[] {
		if (this.state.isSearching) {
			// Only showing the search spinner currently -> don't do any heavy calculations,
			// just return empty result
			return [];
		}
		const {filterPermissions, isSideGrid, feature} = this.props;

		let items = unfilteredItems;

		if (isSideGrid) {
			const spaceItems = this._appState.app.spaceViewRenderer.spaceItemController.getFilteredItems() as unknown as T[];

			items = spaceItems.filter((item) => item.ownFeature === feature);
		} else {
			const itemCount = items.length;

			// Use local, applied filters
			items = filterModels(items, this.selectedView.filters, this._appState);
		}

		// Permissions
		if (filterPermissions) {
			items = items.filter(filterPermissions);
		}

		if (feature === XyiconFeature.Report) {
			items = unfilteredItems.filter((item) => {
				const {scope, isUserDefined} = item as IModel as Report;
				return !(scope === ReportScope.CurrentPortfolio && isUserDefined);
			});
		}

		return items;
	}

	private onKeyUp = (event: KeyboardEvent) => {
		const {overlayedDetailedItems} = this._appState.app;

		switch (event.key) {
			case KeyboardListener.KEY_BACKSPACE:
			case KeyboardListener.KEY_DELETE:
				if (this.selectedItems.length > 0) {
					this.onDeleteClick();
				}
				break;
			case KeyboardListener.KEY_ESCAPE:
				if (!KeyboardListener.isEventTargetAnInput(event) && overlayedDetailedItems?.length === 0) {
					this.setFocusedItems([]);
					if (this.selectedScreenType === ScreenType.CARD) {
						this.setSelectedItems([]);
					}
					this.setState({
						creating: false,
					});
				}
				break;
		}
	};

	private getNumberOfSelectedItems() {
		return this._appState.actions.getNumberOfModels(this.selectedItems);
	}

	private readonly onDeleteClick = async () => {
		if (!this._isDeletePopupWindowOpen && PopupWindowV5.numberOfOpenPopups < 1) {
			const {feature, canDeleteReports, transport} = this.props;
			const {selectedItems} = this;
			let isDeletingAllowed = true;

			this._isDeletePopupWindowOpen = true;

			if (feature === XyiconFeature.XyiconCatalog) {
				const isSafeToDeleteResponse = await transport.requestForOrganization({
					url: "xyiconcatalogs/issafetodelete",
					method: XHRLoader.METHOD_POST,
					params: {
						xyiconCatalogIDList: selectedItems.map((catalog) => catalog.id),
					},
				});

				if (isSafeToDeleteResponse.result?.inUseList.length === selectedItems.length) {
					isDeletingAllowed = false;
					const isSingle = selectedItems.length === 1;

					await WarningWindowV5.open(
						`You can't delete ${isSingle ? "this" : "these"} item${isSingle ? "" : "s"}, because ${isSingle ? "it's" : "they're"} already used as xyicons.`,
					);
				}
			} else if (feature === XyiconFeature.Report && !canDeleteReports(selectedItems)) {
				isDeletingAllowed = false;

				await WarningWindowV5.open(`Sorry, you don't have permission to delete this report.`);
			}

			if (isDeletingAllowed) {
				if (feature === XyiconFeature.Report && selectedItems.some((item) => (item as IModel as Report).isGlobal)) {
					this.setState({isGlobalActionPanelOpen: true});
				} else {
					const count = this.getNumberOfSelectedItems();
					const confirmed = await PopupUtilsV5.getDeleteConfirmationPopupV5(feature, count);

					if (confirmed) {
						if (feature === XyiconFeature.Space) {
							const {user, organizationIdByAuthData} = transport.appState;
							const localStorageKeyForLastViewedSpace = transport.appState.getLocalStorageKeyForLastViewedSpace(user?.id, organizationIdByAuthData);
							localStorage.removeItem(localStorageKeyForLastViewedSpace);
						}
						await this.deleteItems(selectedItems);
					}
				}
			}

			this._isDeletePopupWindowOpen = false;
		}
	};

	private async deleteItems(items: T[]) {
		const res = await this._appState.actions.deleteItems(items, this.props.feature);

		return this.refreshList();
	}

	// ActionBar

	private readonly onActionBarClick = (item: IActionBarItem<T>) => {
		if (item.onClick) {
			item.onClick(this.selectedItems);
		} else {
			const handler = this._actionBar.handlers[item.id];

			if (handler) {
				handler();
			} else if (item.id !== "fieldUpdate") {
				// fieldUpdate is handled in the GridActionBarV5 component
				console.warn("No handler and default onClick for actionBar", item);
			}
		}
	};

	private readonly canImport = () => {
		const {feature} = this.props;
		const {actions, user} = this._appState;

		if (user?.isAdmin) {
			return true;
		}

		//TODO?: add this logic to getFieldPermission and hasAnyTypeTheGivenPermissionInModule
		const permissionSet: PermissionSet = actions.getIndividualPortfolioPermissionSet(this._appState.activePortfolioId);

		if (permissionSet) {
			if (!permissionSet.isAccessEnabled(feature)) {
				return false;
			}
		} else {
			const groupPermissionSet = actions.getPortfolioGroupPermissionSets(this._appState.activePortfolioId)[0];

			if (!groupPermissionSet?.isAccessEnabled(feature)) {
				return false;
			}
		}

		if (actions.hasAnyTypeTheGivenPermissionInModule(feature, Permission.Update)) {
			const fields = actions.getFieldsByFeature(feature);

			return fields.some((field) => actions.getFieldPermission(field) >= Permission.Update);
		}
		return false;
	};

	private readonly onDuplicateClick = (model: T, isCreatePanelNeeded: boolean) => {
		if (model) {
			this._creationCount++;
			this.setState({creating: isCreatePanelNeeded});
		}
	};

	private readonly onActionBarEditClick = () => {
		const model = this.selectedItems[0];

		if (model) {
			if (model.ownFeature === XyiconFeature.XyiconCatalog) {
				this.onCatalogEditClick(model as IModel as Catalog);
			} else {
				this._creationCount++;
				this.setState({creating: true});
				this.props.onEdit(model);
			}
		}
	};

	private readonly onActionBarDuplicateClick = async (item?: T) => {
		if (this.props.feature === XyiconFeature.Portfolio) {
			const confirm = await ConfirmWindowV5.open(
				"Duplicating the selected portfolio will also duplicate the connected spaces, xyicons, boundaries, links, and markups. Do you wish to continue?",
				"Confirm Portfolio Duplication",
			);

			if (confirm) {
				this.onDuplicateClick(item ?? this.selectedItems[0], false);
			}
		} else if (this.props.feature === XyiconFeature.XyiconCatalog) {
			this.onDuplicateCatalogClick((item ?? this.selectedItems[0]) as IModel as Catalog);
		} else {
			this.onDuplicateClick(item ?? this.selectedItems[0], true);
		}
	};

	private readonly onAddClick = () => {
		if (this.props.feature === XyiconFeature.Xyicon) {
			this.props.setCatalogPanelOpen?.(true);
		} else if (this.props.feature === XyiconFeature.Space) {
			CreateSpaceUtils.triggerSpaceCreatePopup();
		} else {
			this.switchToCreatingMode();
			this.props.onAdd?.();
		}
	};

	private readonly onImportClick = () => {
		this.setState({
			isFeatureImportPanelOpen: true,
		});
	};

	public switchToCreatingMode() {
		this._creationCount++;
		this.setState({creating: true});
	}

	// private onSplitterChange = (ratios: number[]) =>
	// {
	// 	const {transport} = this.props;

	// 	transport.services.localStorage.setSplitterRatios(ratios);
	// };

	private readonly onTableSearch = (value: string) => {
		const {feature} = this.props;
		const list = this._appState.actions.getList(feature);

		if (list.length > Debouncer.limit) {
			// search would be too slow to trigger for each keystroke if the list is long -> add debouncer
			// First wait until user stopped typing
			this._typingDebouncer.debounce(() => {
				// User stopped typing

				if (this._appState.actions.areModelsCached()) {
					// models are cached -> no need to show spinner because the search will be fast
					this.setState({
						searchString: value,
						isSearching: false,
					});
				} else {
					// show searching is in progress
					this.setState({isSearching: true});

					// Wait one more second before search starts to allow the search spinner to be rendered
					// (UI gets frozen during search so it helps the user to see a spinner)
					this._listDebouncer.debounceList(() => {
						// spinner is now visible, now start the search by setting the state
						this.setState({
							searchString: value,
							isSearching: false,
						});
					}, list);
				}
			});
		} else {
			this.setState({
				searchString: value,
				isSearching: false,
			});
		}
	};

	private get selectedView() {
		const viewFeature = this.props.isSideGrid ? XyiconFeature.SpaceEditor : this.props.feature;

		return this._appState.actions.getSelectedView(viewFeature);
	}

	private readonly onCatalogEditClick = async (catalog: Catalog) => {
		if (!this._isLoadingCatalogEditor) {
			this._isLoadingCatalogEditor = true;

			const iconConfig = await this.props.transport.getIconConfigOfCatalog(catalog);

			this.setState({
				editedCatalog: catalog,
				iconConfig: iconConfig,
				catalogMode: "edit",
			});

			this._isLoadingCatalogEditor = false;
		}
	};

	private readonly onDuplicateCatalogClick = async (catalog: Catalog) => {
		if (!this._isLoadingCatalogEditor) {
			this._isLoadingCatalogEditor = true;
			const iconConfig = await this.props.transport.getIconConfigOfCatalog(catalog);

			this.setState({
				editedCatalog: catalog,
				iconConfig: iconConfig,
				catalogMode: "create",
			});

			this._isLoadingCatalogEditor = false;
		}
	};

	private readonly onAddCatalogClick = (event: React.MouseEvent) => {
		if (event.target !== this._appState.catalogEditorParentMaybe) {
			this._appState.catalogEditorParentMaybe = event.currentTarget as HTMLDivElement;
		}

		this.setState({
			editedCatalog: null,
			iconConfig: null,
			catalogMode: "create",
		});
	};

	private readonly onCloseCatalogEditor = () => {
		this.setState({
			editedCatalog: null,
			iconConfig: null,
			catalogMode: null,
		});
	};

	private readonly onCatalogDockableSetOpen = (isOpen: boolean) => {
		if (!isOpen) {
			this.onCloseCatalogEditor();
			this._appState.catalogEditorParentMaybe = null;
		}
		this.props.setCatalogPanelOpen?.(isOpen);
	};

	private readonly onCatalogDockableSetDocked = (isDocked: boolean) => {
		this.onCloseCatalogEditor();
		this.props.setCatalogPanelDocked?.(isDocked);
	};

	private getCatalogPanel() {
		const style: React.CSSProperties = {};

		if (this.props.isCatalogPanelDocked) {
			style.height = "100%";
			style.transform = "initial";
			style.borderRight = `1px solid ${colorPalette.gray.c300}`;
		} else {
			style.left = "initial";
			style.right = "16px";
			style.top = "65px";
			style.transform = "initial";
		}

		return (
			<DockableV5
				style={style}
				spaceViewRenderer={this.props.app.spaceViewRenderer}
				gridView={this._gridView}
				setDocked={this.onCatalogDockableSetDocked}
				setOpen={this.onCatalogDockableSetOpen}
				isDocked={this.props.isCatalogPanelDocked}
				title="Catalog"
				setActiveTool={Functions.emptyFunction}
				onAddCatalogClick={this.onAddCatalogClick}
				onDuplicateCatalogClick={this.onDuplicateCatalogClick}
				onCreateUnplottedXyicons={this.onCreateUnplottedXyicons}
			/>
		);
	}

	private get table() {
		return this._gridView.current?.table;
	}

	public override async componentDidMount() {
		ensureSpaceEditorDockMainSeededFromLegacyCatalogKey();
		this._isMounted = true;
		this._lastViewId = this.selectedView?.id ?? "";
		if (!this._appState.lists[this.props.feature].loaded) {
			await this.refreshList();
		}

		if (this._isMounted) {
			KeyboardListener.getInstance().signals.up.add(this.onKeyUp);
		}
	}

	public override async componentDidUpdate(prevProps: IModuleViewProps<T>, prevState: IModuleViewState<T>) {
		const {transport, feature, appState} = this.props;

		if (appState !== this._appState) {
			this._appState = appState;
		}

		if (this._appState.lists[feature]?.loaded === false) {
			await transport.services.feature.refreshList(feature);
		}

		if ((this.props.selectedItems && !this.props.setSelectedItems) || (!this.props.selectedItems && this.props.setSelectedItems)) {
			console.warn("You should either provide both selectedItems and setSelectedItems, or shouldn't provide either of them!");
		}

		if ((this.props.focusedItems && !this.props.setFocusedItems) || (!this.props.focusedItems && this.props.setFocusedItems)) {
			console.warn("You should either provide both focusedItems and setFocusedItems, or shouldn't provide either of them!");
		}

		if (
			this.state.focusedItems.length > 0 &&
			this._portfolioId !== this._appState.activePortfolioId &&
			this.props.feature !== XyiconFeature.Portfolio
		) {
			this.setState({focusedItems: []});
		}

		if (prevState.isPortTemplateEditorOpen && !ObjectUtils.compare(prevState.focusedItems, this.focusedItems)) {
			this.setState({isPortTemplateEditorOpen: false});
		}
		this._portfolioId = this._appState.activePortfolioId;

		const currentViewId = this.selectedView?.id ?? "";

		if (currentViewId !== this._lastViewId) {
			this._lastViewId = currentViewId;
			this.syncSelectionWithCurrentView();
		}

		if (this.props.feature !== prevProps.feature) {
			this._pendingTableFocus = null;
		}
	}

	public override componentWillUnmount() {
		this._isMounted = false;
		this._pendingTableFocus = null;
		KeyboardListener.getInstance().signals.up.remove(this.onKeyUp);
	}

	private readonly resetFocusedItemsArray = () => {
		this.setFocusedItems([]);
		if (this.selectedScreenType === ScreenType.CARD) {
			this.setSelectedItems([]);
		}
	};

	private readonly sortItemsById = (a: T, b: T) => StringUtils.sortIgnoreCase(a.id, b.id);

	private get areSelectedAndFocusedItemsTheSame() {
		return ObjectUtils.compare(this.focusedItems.toSorted(this.sortItemsById), this.selectedItems.toSorted(this.sortItemsById));
	}

	public get isLoadingList() {
		return !this._appState.lists[this.props.feature].loaded || this.state.initialLoading || this.state.isLoadingDependencies;
	}

	public get isMounted() {
		return this._isMounted;
	}

	private readonly onDetailsButtonClick = () => {
		return this.onFocus(!!this.focusedItems.length && this.areSelectedAndFocusedItemsTheSame ? [] : this.selectedItems, false);
	};

	public override render() {
		const {feature, className, actionBar, isSideGrid, appState, detailsHeaderControls, runReport, onEdit} = this.props;
		const {creating, searchString, isSearching, initialLoading, sharingViewId, loadingViewId, isGlobalActionPanelOpen} = this.state;
		const {selectedItems, focusedItems} = this;
		const isLoadingList = this.isLoadingList;
		const loadingView = this._appState.actions.getViewById(loadingViewId);

		if (initialLoading) {
			window.setTimeout(() => {
				this.setState({initialLoading: false});
			}, 500);
		}

		const selectedView = this.selectedView;
		const unfilteredItems: T[] = this._appState.actions.getList(feature);
		const items = this.getFilteredItems(unfilteredItems);
		const creatingOrEditingOrImporting = creating || this.state.catalogMode != null || this.state.isFeatureImportPanelOpen;
		const extendedActionBarButtons = extendActionBarButtonsWithDetailsButton(
			actionBar,
			focusedItems.length > 0 && this.areSelectedAndFocusedItemsTheSame,
			this.onDetailsButtonClick,
		);

		const {type: filterType} = selectedView.filters;
		let currentFiltersCount = 0;

		if (feature !== XyiconFeature.Report) {
			currentFiltersCount = selectedView.getActiveFilterCount(filterType);
		}

		return (
			<ModuleViewStyled className={`ModuleView ${className || ""}`}>
				{!this.props.isCatalogPanelDocked && this.props.isCatalogPanelOpen && this.getCatalogPanel()}
				<div
					className={ReactUtils.cls("createPanel", {
						open: creatingOrEditingOrImporting || this.state.managingColumns,
						manageColumns: this.state.managingColumns,
						import: this.state.isFeatureImportPanelOpen,
					})}
				>
					{this.state.catalogMode === "create" && (
						<CatalogCreateV5
							isOpen={true}
							onClose={this.onCloseCatalogEditor}
							editedCatalogMaybe={{
								catalog: this.state.editedCatalog,
								config: this.state.iconConfig,
							}}
						/>
					)}
					{this.state.catalogMode === "edit" && (
						<CatalogItemPanelV5
							onClose={this.onCloseCatalogEditor}
							catalog={this.state.editedCatalog}
							iconConfig={this.state.iconConfig}
							mode={this.state.catalogMode}
							shape={this.state.editedCatalog?.iconCategory === CatalogIconType.ModelParameter ? "Custom Shape" : "Default Shape"}
							step={this.state.editedCatalog?.iconCategory === CatalogIconType.ModelParameter ? "Item properties" : "Select model"}
						/>
					)}
					{this.state.managingColumns && (
						<ManageColumnsPanelV5
							feature={feature}
							view={selectedView}
							onClose={this.onCloseManageColumns}
							parentRef={this._manageColumnsButtonRef}
						/>
					)}
				</div>
				{sharingViewId && (
					<ViewSharingPopup
						view={this._appState.actions.getViewById(sharingViewId)}
						onClose={() => this.toggleSharingPanel()}
					/>
				)}
				<SplitterV5
					localStorageKey={
						!isSideGrid && this.props.isCatalogPanelOpen && this.props.isCatalogPanelDocked
							? SPLITTER_V5_LOCAL_STORAGE_KEY_SPACE_EDITOR_DOCK_MAIN
							: `v5-splitter-module-view-${feature}-${isSideGrid ? "side-grid" : "main"}`
					}
				>
					{this.props.isCatalogPanelDocked && this.props.isCatalogPanelOpen && this.getCatalogPanel()}
					<MainPanelStyled data-min-width={SPLITTER_V5_CATALOG_MAIN_SECOND_PANE_MIN_WIDTH_PX}>
						<ActionBarV5
							feature={feature}
							items={items}
							onSearchChange={this.onTableSearch}
							createButtonRef={this.props.createButtonRef}
							manageColumnsButtonRef={this._manageColumnsButtonRef}
							create={this.props.create}
							onCloseCreate={this.onCloseCreate}
							filterCount={currentFiltersCount}
							onResetFilters={() => selectedView.removeAllFiltersFromActiveFilterType()}
							isSideGrid={isSideGrid}
							searchString={searchString}
							isLoadingList={isLoadingList}
							onSelect={this.onSelect}
							isDetailsPanelOpen={focusedItems.length > 0}
							closePopupsCallback={this.props.registerClosePopupsHandler}
						/>
						{isLoadingList ? (
							<LoaderV5 label={`Loading ${featureTitlePlurals[feature as keyof typeof featureTitlePlurals]}...`} />
						) : (
							<>
								{loadingView && <LoaderV5 label={`Loading "${loadingView.name}" View`} />}
								{this.selectedScreenType === ScreenType.CARD ? (
									<CardViewV5
										feature={feature}
										items={items}
										selected={selectedItems}
										focused={focusedItems}
										onSelect={this.onSelect}
										onFocus={this.onFocus}
										onOpenSpace={this.onDoubleClick}
										onAddClick={this.onAddClick}
										searchString={searchString}
										actionBarButtons={extendedActionBarButtons}
										actionBarDefaultClick={this.onActionBarClick}
										onCloseGridActionBar={this.onCloseCardActionBar}
									/>
								) : (
									<>
										<GridViewV5
											ref={this._gridView}
											onTableReady={this.onTableReady}
											prepareFieldsInRow={isSideGrid}
											feature={feature}
											items={items}
											selected={selectedItems}
											focused={focusedItems}
											view={selectedView}
											onSelect={this.onSelect}
											onFocus={this.onFocus}
											onDoubleClick={this.onDoubleClick}
											onAddClick={this.props.onAdd && this.onAddClick}
											searchString={searchString}
											isSearching={isSearching}
											tableRowIcon={true}
											isCellContentsWrappingOn={this.state.isCellContentsWrappingOn}
											emptyListText={this.props.emptyListText}
											onManageColumns={this.props.manageColumns !== false && this.onManageColumns}
											actionBarButtons={extendedActionBarButtons}
											actionBarDefaultClick={this.onActionBarClick}
											onCloseGridActionBar={this.onCloseGridActionBar}
											onDuplicateClick={this.onActionBarDuplicateClick}
											runReport={runReport}
											selectedScreen={this.selectedScreenType}
											getItemColor={(item: T) => this._spotlightEvaluator.evaluateSpotlightColor(item, appState, feature, !!isSideGrid)}
											colorApplicationMode="row"
											getSpotlightFieldRefID={(item: T, fieldRefID?: string) => this._spotlightEvaluator.getSpotlightFieldRefID(item, fieldRefID)}
											getColorApplicationMode={(item: T) => this._spotlightEvaluator.getColorApplicationMode(item)}
											getSpotlightFieldColor={(item: T, fieldRefID?: string) =>
												this._spotlightEvaluator.getSpotlightFieldColor(item, appState, fieldRefID)
											}
										/>
										{isGlobalActionPanelOpen && (
											<GlobalSharingPopup
												viewItems={selectedItems as IModel[] as Report[]}
												onClose={() => this.setState({isGlobalActionPanelOpen: false})}
												viewActionType={"delete"}
												isReports={true}
												onConfirm={this.refreshList}
											/>
										)}
									</>
								)}
							</>
						)}
					</MainPanelStyled>
				</SplitterV5>
				{this.props.manageDetailsPanel && (
					<Panel
						isOpen={focusedItems.length > 0 && !initialLoading}
						panelId="module-view-details-panel"
					>
						<DetailsContentV5
							ref={this._detailsContent}
							items={focusedItems}
							feature={feature}
							features={[feature]}
							isPortTemplateEditorOpen={this.state.isPortTemplateEditorOpen}
							setPortTemplateEditorOpen={(value) => this.setState({isPortTemplateEditorOpen: value})}
							onCatalogIconEditClick={this.onCatalogEditClick}
							onSelect={this.onSelect}
							onFocus={this.onFocus}
							onCloseOverlayedDetaislPanel={this.resetFocusedItemsArray}
							noInitials={feature === XyiconFeature.Report}
							additionalHeaderControls={detailsHeaderControls}
						/>
					</Panel>
				)}
			</ModuleViewStyled>
		);
	}
}

const ModuleViewStyled = styled.div`
	display: flex;
	position: relative;
	min-width: 0;
	flex: 1;
	height: 100%;

	.sharingPanel,
	.createPanel {
		display: none;
	}
`;

const MainPanelStyled = styled.div`
	${VerticalFlexStyle};
	max-height: calc(100vh - 64px);
`;
